import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { StyledTextField } from 'components/material-ui/_overrideStyles';
import useFieldValidation from 'hooks/useFieldValidation';
import PropTypes from 'prop-types';
import React from 'react';
import { dismissOnSubmitKey } from 'utils/helper';

export function isNumeric(str) {
	return /^\d+$/.test(str);
}

const hasNumbersOnly = value => {
	const regex = /[^0-9]/;
	return !regex.test(value);
};

export function getParsedValue(mask, value) {
	switch(mask) {
		case '##-###':
			let valuePostalCode = value.replace('-', '123456789').replace(/[^0-9]+/g, '').replace('123456789', '-');
			if(valuePostalCode?.length === 2) {
				return `${valuePostalCode}-`;
			} else if(valuePostalCode?.length === 3 || valuePostalCode?.length > 6) {
				return valuePostalCode.substr(0, value?.length - 1);
			}
			return valuePostalCode;
		default:
			// eslint-disable-next-line no-console
			console.log('Mask doesn\'t found.');
			return value;
	}
}

export default function TextField({
	name, form, errors, loading, disabled, defaultHelper,
	onChange, onBlur, InputProps, inputProps, dismissOnSubmit,
	isNumberWithoutSigns, isNumberString, parsingFunc, mask, ...props
}) {
	const value = form?.values[name];
	const { isError, helperText, resetField } = useFieldValidation({ form, name, errors, defaultHelper });

	const handleOnChange = event => {
		let caretPosition = event.target.selectionStart;
		let targetValue = event.target.value;
		const currentTarget = event.target;

		resetField();

		if(typeof parsingFunc === 'function') {
			targetValue = parsingFunc(targetValue);
		}
		if(mask) {
			targetValue = getParsedValue(mask, targetValue);

			if((Math.abs(Number(targetValue.length) - Number(event.target.value.length)) === 1) && (mask.length !== targetValue.length)) {
				caretPosition++;
			}
			// Fix caret position
			window.requestAnimationFrame(() => {
				currentTarget.selectionStart = caretPosition;
				currentTarget.selectionEnd = caretPosition;
			});
		}

		const newEvent = { ...event, target: { ...event.target, name, value: targetValue }};
		onChange ? onChange(newEvent) : form?.handleChange(newEvent);
	};

	return (
		<StyledTextField
			data-cy={props['data-cy'] ?? name}
			value={value}
			error={isError}
			disabled={loading || disabled}
			helperText={helperText}
			onChange={handleOnChange}
			onBlur={event => {
				!isError && onBlur && onBlur(event);
			}}
			InputProps={{
				endAdornment: loading && (
					<InputAdornment position="end">
						<CircularProgress size='1rem' color='secondary' />
					</InputAdornment>
				),
				...InputProps,
			}}
			inputProps={{
				onKeyPress: event => {
					if(isNumberWithoutSigns && (event?.key === '-' || event?.key === '+')) event.preventDefault();
					if(isNumberString && !hasNumbersOnly(event?.key) && event?.key !== '-' && event?.key !== '+') event.preventDefault();
					if(dismissOnSubmit) dismissOnSubmitKey(event);
				},
				...inputProps,
			}}
			{...props}
		/>
	);
}

TextField.propTypes = {
	name: PropTypes.string.isRequired,
	onChange: PropTypes.func,
	value: PropTypes.string,
	touched: PropTypes.bool,
	errorText: PropTypes.string,
	id: PropTypes.string,
	label: PropTypes.node,
	variant: PropTypes.string,
	type: PropTypes.string,
	inputProps: PropTypes.object,
	fullWidth: PropTypes.bool
};

TextField.defaultProps = {
	defaultHelper: '',
	dismissOnSubmit: false,
	variant: 'standard',
	fullWidth: false,
	type: 'text',
	size: 'small'
};
